import { ElementRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentIntentResult, Stripe, StripeElements, StripeError, loadStripe } from '@stripe/stripe-js';
import { environment } from 'projects/igsu-portal/src/environments/environment';
import { Observer, catchError, take } from 'rxjs';
import { ModalService } from '../../_shared/data-access/modal.service';
import { EventsService } from '../../events/data-access/events.service';
import { PaymentAPI } from './_payment.api';
import { PaymentService } from './payment.service';

/**
 * @deprecated Stripe is not being used anymore.
 */
@Injectable({
  providedIn: 'root',
})
export class StripeService {
  private _api = inject(PaymentAPI);
  private _router = inject(Router);
  private _eventService = inject(EventsService);
  private _modalService = inject(ModalService);
  private _paymentService = inject(PaymentService);

  private stripe: Stripe | null = null;
  public stripeInstance: StripeElements | null = null;

  private routeToNavigateBack = '/seat-selection';

  public initialize = async (stripeContainer: ElementRef): Promise<void> => {
    const cartId = this._paymentService.getTransaction().cartId;
    if (!cartId) {
      return console.error('No cart id');
    }
    try {
      this._api
        .getStripeToken(cartId)
        .pipe(take(1), catchError(this._paymentService.onPaymentProviderError))
        .subscribe(async piKey => {
          const stripe = await this.loadStripe();
          const clientSecret = this._eventService.getEventConfiguration()?.secretKey;
          if (!clientSecret) {
            return console.error('Secreto no encontrado');
          }

          this.setupElements(stripe, clientSecret, piKey, stripeContainer);
        });
    } catch (error) {
      console.error('Stripe initialization error:', error);
    }
  };

  public confirmPayment = async (setIsProccessing: (isProcessing: boolean) => void) => {
    if (!this.stripeInstance) {
      return;
    }

    setIsProccessing(true);

    const validationError = await this.isValidationError();

    if (validationError) {
      this.showValidationErrorModal(validationError);
      setIsProccessing(false);
      return;
    }

    const paymentData = await this.stripe?.confirmPayment({
      elements: this.stripeInstance,
      redirect: 'if_required',
    });

    this.handlePaymentResult(paymentData, setIsProccessing);
  };

  private showValidationErrorModal(error: StripeError) {
    const modalData = {
      type: 'app',
      text: error.message!,
      buttonText: 'Retry',
      buttonAction: () => this._modalService.closeModal(),
    };
    this._modalService.openModal(modalData);
  }

  private handlePaymentResult(
    paymentData: PaymentIntentResult | undefined,
    setIsProccessing: (isProcessing: boolean) => void
  ) {
    if (paymentData?.error) {
      this.handlePaymentError(paymentData.error, setIsProccessing);
    } else if (paymentData?.paymentIntent) {
      this.processPaymentIntent(paymentData.paymentIntent.id, setIsProccessing);
    }
  }

  private handlePaymentError(error: StripeError, setIsProccessing: (isProcessing: boolean) => void) {
    this._paymentService.errorMessage = error.message!;
    this._paymentService.handlePaymentFail();
    setIsProccessing(false);
  }

  private processPaymentIntent(paymentIntentId: string, setIsProccessing: (isProcessing: boolean) => void) {
    const observer: Partial<Observer<PaymentIntentResult>> = {
      next: data => this.handleSuccessfulPayment(data, setIsProccessing),
      error: error => this.handlePaymentObserverError(error, setIsProccessing),
    };
    const observable = this._paymentService.checkoutOnRequest(paymentIntentId);
    observable.subscribe(observer);
  }

  private handleSuccessfulPayment = (data: PaymentIntentResult, setIsProccessing: (isProcessing: boolean) => void) => {
    this._paymentService.handlePaymentSuccess();
    setIsProccessing(false);
  };

  private handlePaymentObserverError = (error: any, setIsProccessing: (isProcessing: boolean) => void) => {
    this._paymentService.handlePaymentFail();
    setIsProccessing(false);
  };

  private isValidationError = async (): Promise<StripeError | undefined> => {
    const submitResult = await this.stripeInstance?.submit();
    return submitResult?.error;
  };

  private loadStripe = async (): Promise<Stripe> => {
    const stripe = await loadStripe(environment.stripePublicKey);
    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }
    return stripe;
  };

  /**
   * Reset Stripe component back to its null state
   */
  public resetStripeComponent = () => {
    this.stripeInstance = null;
  };

  private setupElements = (
    stripe: Stripe,
    clientSecret: string,
    piKey: string | undefined,
    stripeContainer: ElementRef
  ): void => {
    if (piKey === undefined) {
      return;
    }

    const elements = stripe.elements({ clientSecret: piKey, appearance: { theme: 'stripe' } });
    this.stripe = stripe;
    this.stripeInstance = elements;
    this.mountStripeElement(elements, stripeContainer);
  };

  private mountStripeElement = (elements: StripeElements, stripeContainer: ElementRef): void => {
    const paymentElement = elements.create('payment', { business: { name: 'MMC' } });
    paymentElement.mount(stripeContainer.nativeElement);
  };
}
